'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { cn, isEdge } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useTransition } from 'react';
import { UnauthQueryOptions } from '@/api';
import { buildSingleOrderSearchParams } from '@/app/donation/_utils/singleOrder';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, ROUTES, STORAGE_KEY } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { useAuthStore } from '@/stores/AuthStore';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { DonationReservationCompleteModal } from '../DonationReservationCompleteModal';
import { DonationReservationModal } from '../DonationReservationModal';
import { useIsDonationHistory } from './_hooks/useIsDonationHistory';
import { AgreementSection } from './AgreementSection';
import { PrefillForm } from './PrefillForm';
interface DonationButtonProps {
  institutionId: number;
  donationBusinessId?: number;
  institutionName: string;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}
export const DonationButton = t0 => {
  const $ = _c(96);
  const {
    institutionId,
    donationBusinessId,
    institutionName,
    disabled,
    children,
    style,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const isLogin = useAuthStore(_temp);
  const {
    isDonationHistory
  } = useIsDonationHistory();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isEdge();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isEdgeBrowser = t1;
  const [, startTransition] = useTransition();
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isReservationCompleteModalOpen, setIsReservationCompleteModalOpen] = useState(false);
  const router = useCustomRouter();
  let t2;
  if ($[1] !== isLogin) {
    t2 = {
      enabled: isLogin
    };
    $[1] = isLogin;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    checkVerification,
    isLoading: isVerificationLoading
  } = useVerificationCheck(t2);
  const pathname = usePathname();
  const [step, setStep] = useState("closed");
  const [isEdgeModal, setIsEdgeModal] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/control-managements"](),
      select: _temp2,
      staleTime: 0,
      gcTime: 0
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: donationReservation
  } = useSuspenseQuery(t3);
  let t4;
  if ($[4] !== checkVerification || $[5] !== donationReservation || $[6] !== isLogin || $[7] !== pathname || $[8] !== router) {
    t4 = () => {
      if (isEdgeBrowser) {
        setIsEdgeModal(true);
        return;
      }
      if (!isLogin) {
        router.push(ROUTES.LOGIN.withSearchParams({
          searchParams: {
            redirect: pathname ?? ""
          }
        }));
        return;
      }
      if (checkVerification()) {
        return;
      }
      if (donationReservation) {
        setStep("closed");
        setIsReservationModalOpen(true);
        return;
      }
      setStep("product-check");
    };
    $[4] = checkVerification;
    $[5] = donationReservation;
    $[6] = isLogin;
    $[7] = pathname;
    $[8] = router;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleStart = t4;
  let t5;
  if ($[10] !== isDonationHistory) {
    t5 = () => {
      const isAgreement = sessionStorage.getItem(STORAGE_KEY.DONATION_AGREEMENT);
      if (!isDonationHistory && !isAgreement) {
        setStep("agreement");
        return;
      }
      setStep("prefill");
    };
    $[10] = isDonationHistory;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleDonationWithoutGift = t5;
  let t6;
  if ($[12] !== donationBusinessId || $[13] !== institutionId || $[14] !== institutionName || $[15] !== router) {
    t6 = () => {
      sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildSingleOrderSearchParams({
          donationBusinessId,
          institutionId,
          institutionName
        })
      }));
    };
    $[12] = donationBusinessId;
    $[13] = institutionId;
    $[14] = institutionName;
    $[15] = router;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleDonationWithoutGiftAfterAgreement = t6;
  let t7;
  if ($[17] !== className) {
    t7 = cn("w-full", className);
    $[17] = className;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = children || "\uAE30\uBD80\uD558\uAE30";
  let t9;
  if ($[19] !== disabled || $[20] !== handleStart || $[21] !== isVerificationLoading || $[22] !== style || $[23] !== t7 || $[24] !== t8) {
    t9 = <Button loading={isVerificationLoading} onClick={handleStart} className={t7} disabled={disabled} style={style}>{t8}</Button>;
    $[19] = disabled;
    $[20] = handleStart;
    $[21] = isVerificationLoading;
    $[22] = style;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = step === "agreement";
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setStep("closed");
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      title: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== handleDonationWithoutGiftAfterAgreement) {
    t13 = <AgreementSection onNext={handleDonationWithoutGiftAfterAgreement} />;
    $[28] = handleDonationWithoutGiftAfterAgreement;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t13) {
    t14 = <ResponsiveDialog opened={t10} onClose={t11} drawerType="bottom" title={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"} modalHeader={t12}>{t13}</ResponsiveDialog>;
    $[30] = t10;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = step === "product-check";
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setStep("closed");
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      title: "\uAE30\uBD80 \uAE08\uC561\uC5D0 \uB9DE\uB294\n\uB2F5\uB840\uD488\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
      description: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80\uAE08\uC758 30% \uB0B4\uC5D0\uC11C \uB2F5\uB840\uD488\uC744 \uBB34\uB8CC\uB85C \uBC1B\uC544\uBCF4\uC138\uC694!"
    };
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isDesktopViewport) {
    t18 = !isDesktopViewport ? <><h3 className="mb-26 break-keep pb-12 text-center font-700 text-Gray-900 typo-body1">기부 금액에 맞는<br />답례품을 선택해 주세요</h3><p className="mb-32 text-center font-400 text-Gray-800 typo-body3">고향사랑기부금의 30% 내에서<br />답례품을 무료로 받아보세요!</p></> : null;
    $[35] = isDesktopViewport;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setStep("closed");
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== institutionId) {
    t20 = DONATION_ROUTES.HOMETOWNS_DETAIL.withSearchParams({
      dynamicPath: {
        id: institutionId
      },
      searchParams: {
        tab: "gifts"
      }
    });
    $[38] = institutionId;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== institutionName) {
    t21 = gtmDatasets.startGift({
      local_name: institutionName
    });
    $[40] = institutionName;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t20 || $[43] !== t21) {
    t22 = <Button dimensions="H48" asChild={true}><Link onClick={t19} href={t20} {...t21}>답례품 선택 후 기부하기</Link></Button>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== institutionName) {
    t23 = gtmDatasets.startDonation({
      local_name: institutionName
    });
    $[45] = institutionName;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== handleDonationWithoutGift || $[48] !== t23) {
    t24 = <Button dimensions="H48" theme="sub-text" onClick={handleDonationWithoutGift} {...t23}>답례품 없이 기부하기</Button>;
    $[47] = handleDonationWithoutGift;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t22 || $[51] !== t24) {
    t25 = <div className="flex flex-col gap-8">{t22}{t24}</div>;
    $[50] = t22;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t15 || $[54] !== t18 || $[55] !== t25) {
    t26 = <ResponsiveDialog opened={t15} onClose={t16} drawerType="bottom" modalHeader={t17}>{t18}{t25}</ResponsiveDialog>;
    $[53] = t15;
    $[54] = t18;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const t27 = step === "prefill";
  let t28;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setStep("closed");
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      title: "\uAE30\uBD80\uAE08 \uC120\uD0DD"
    };
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== donationBusinessId || $[60] !== institutionId || $[61] !== institutionName || $[62] !== router) {
    t30 = data_0 => {
      setStep("closed");
      sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildSingleOrderSearchParams({
          donationBusinessId,
          institutionId,
          institutionName,
          initialAmount: data_0.amount,
          initialPurpose: data_0.purpose
        })
      }));
    };
    $[59] = donationBusinessId;
    $[60] = institutionId;
    $[61] = institutionName;
    $[62] = router;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== donationBusinessId || $[65] !== institutionId || $[66] !== t30) {
    t31 = <PrefillForm institutionId={institutionId} defaultDonationBusinessId={donationBusinessId} onSubmit={t30} />;
    $[64] = donationBusinessId;
    $[65] = institutionId;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t27 || $[69] !== t31) {
    t32 = <ResponsiveDialog opened={t27} onClose={t28} drawerType="bottom" modalHeader={t29} title={"\uAE30\uBD80\uAE08 \uC120\uD0DD"}>{t31}</ResponsiveDialog>;
    $[68] = t27;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setIsEdgeModal(false);
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  let t35;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <h2 className="font-700 text-Gray-900 typo-body2">웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.</h2>;
    t35 = <p className="font-400 text-Gray-700 typo-body3">원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.</p>;
    $[72] = t34;
    $[73] = t35;
  } else {
    t34 = $[72];
    t35 = $[73];
  }
  let t36;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="flex flex-col gap-16 text-center">{t34}{t35}<Button onClick={() => setIsEdgeModal(false)}>확인</Button></div>;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  let t37;
  if ($[75] !== isEdgeModal) {
    t37 = <Modal opened={isEdgeModal} onClose={t33}>{t36}</Modal>;
    $[75] = isEdgeModal;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  let t38;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setIsReservationModalOpen(false);
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== startTransition) {
    t39 = () => {
      setIsReservationModalOpen(false);
      startTransition(() => {
        setIsReservationCompleteModalOpen(true);
      });
    };
    $[78] = startTransition;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  const t40 = donationReservation?.contents;
  let t41;
  if ($[80] !== donationBusinessId || $[81] !== isReservationModalOpen || $[82] !== t39 || $[83] !== t40) {
    t41 = <DonationReservationModal opened={isReservationModalOpen} businessId={donationBusinessId} onClose={t38} onSuccess={t39} description={t40} />;
    $[80] = donationBusinessId;
    $[81] = isReservationModalOpen;
    $[82] = t39;
    $[83] = t40;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsReservationCompleteModalOpen(false);
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] !== isReservationCompleteModalOpen) {
    t43 = <DonationReservationCompleteModal opened={isReservationCompleteModalOpen} onClose={t42} />;
    $[86] = isReservationCompleteModalOpen;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] !== t14 || $[89] !== t26 || $[90] !== t32 || $[91] !== t37 || $[92] !== t41 || $[93] !== t43 || $[94] !== t9) {
    t44 = <>{t9}{t14}{t26}{t32}{t37}{t41}{t43}</>;
    $[88] = t14;
    $[89] = t26;
    $[90] = t32;
    $[91] = t37;
    $[92] = t41;
    $[93] = t43;
    $[94] = t9;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  return t44;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(data) {
  return data.context;
}